import React, { useState, useEffect, useCallback, useRef } from 'react';
import { get, post } from '../../services/smartApiService';
import { AI_TRADER_URLS } from '../../api/AiTraderUrls';
import AIDecisionPanel from './components/AIDecisionPanel';
import SignalScorecard from './components/SignalScorecard';
import OILevelsChart from './components/OILevelsChart';
import IndexWeightMap from './components/IndexWeightMap';
import LiveFeeds from './components/LiveFeeds';
import GlobalMarketsBar from './components/GlobalMarketsBar';
import AITraderChat from './components/AITraderChat';
import SignalHistory from './components/SignalHistory';
import MorningBriefing from './components/MorningBriefing';

const AUTO_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes — matches signal cron cadence

// Helper: wrap Observable into Promise resolving to response.data
const obs2p = (obs: any): Promise<any> =>
  new Promise((resolve, reject) =>
    obs.subscribe({ next: (r: any) => resolve(r?.data ?? r), error: reject })
  );

const isMarketOpen = (): 'OPEN' | 'CLOSED' | 'PRE' => {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = ist.getDay();
  const mins = ist.getHours() * 60 + ist.getMinutes();
  if (day === 0 || day === 6) return 'CLOSED';
  if (mins >= 570 && mins <= 930) return 'OPEN';
  if (mins >= 540 && mins < 570) return 'PRE';
  return 'CLOSED';
};

export default function AITraderPage() {
  const [briefing, setBriefing] = useState<any>(null);
  const [signals, setSignals] = useState<Record<string, any>>({});
  const [globalMarket, setGlobalMarket] = useState<any>(null);
  const [oiLevels, setOiLevels] = useState<Record<string, any>>({});
  const [oiChains, setOiChains] = useState<Record<string, any>>({});
  const [weightMaps, setWeightMaps] = useState<Record<string, any>>({});
  const [sentiment, setSentiment] = useState<Record<string, any>>({});
  const [twitterFeeds, setTwitterFeeds] = useState<Record<string, any[]>>({});
  const [signalHistory, setSignalHistory] = useState<Record<string, any[]>>({});
  const [activeSymbol, setActiveSymbol] = useState<'NIFTY' | 'BANKNIFTY'>('NIFTY');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [signalRefreshing, setSignalRefreshing] = useState<Record<string, boolean>>({});
  const [sentimentRefreshing, setSentimentRefreshing] = useState(false);
  const [globalRefreshing, setGlobalRefreshing] = useState(false);
  const [sentimentLastUpdated, setSentimentLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [marketStatus, setMarketStatus] = useState<'OPEN' | 'CLOSED' | 'PRE'>(isMarketOpen());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevSignalsRef = useRef<Record<string, any>>({});

  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const results = await Promise.allSettled([
        obs2p(get(AI_TRADER_URLS.BRIEFING, { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SIGNAL_ALL, { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.GLOBAL_MARKET, { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.OI_LEVELS('NIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.OI_LEVELS('BANKNIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.OI_CHAIN('NIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.OI_CHAIN('BANKNIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.WEIGHT_MAP('NIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.WEIGHT_MAP('BANKNIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT('NIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT('BANKNIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT_FEEDS('NIFTY', 15), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT_FEEDS('BANKNIFTY', 15), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SIGNAL_HISTORY('NIFTY', 12), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SIGNAL_HISTORY('BANKNIFTY', 12), { showLoading: false })),
      ]);

      const [brief, allSig, global, niftyOI, bnkOI, niftyChain, bnkChain,
             niftyWt, bnkWt, niftySent, bnkSent,
             niftyFeeds, bnkFeeds, niftyHist, bnkHist] = results;

      if (brief.status === 'fulfilled') setBriefing(brief.value);
      if (allSig.status === 'fulfilled') {
        const newSignals = allSig.value || {};
        // Fire browser notification if direction changed since last fetch
        Object.entries(newSignals).forEach(([sym, sig]: [string, any]) => {
          const prev = prevSignalsRef.current[sym];
          if (prev && sig && prev.direction !== sig.direction && sig.direction !== 'WAIT') {
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`${sym} Signal Changed!`, {
                body: `${prev.direction} → ${sig.direction} | Strike: ${sig.strike} | Confidence: ${sig.confidence}/10`,
                icon: '/favicon.ico',
              });
            }
          }
        });
        prevSignalsRef.current = newSignals;
        setSignals(newSignals);
      }
      if (global.status === 'fulfilled') setGlobalMarket(global.value);
      if (niftyOI.status === 'fulfilled') setOiLevels(prev => ({ ...prev, NIFTY: niftyOI.value }));
      if (bnkOI.status === 'fulfilled') setOiLevels(prev => ({ ...prev, BANKNIFTY: bnkOI.value }));
      if (niftyChain.status === 'fulfilled') setOiChains(prev => ({ ...prev, NIFTY: niftyChain.value }));
      if (bnkChain.status === 'fulfilled') setOiChains(prev => ({ ...prev, BANKNIFTY: bnkChain.value }));
      if (niftyWt.status === 'fulfilled') setWeightMaps(prev => ({ ...prev, NIFTY: niftyWt.value }));
      if (bnkWt.status === 'fulfilled') setWeightMaps(prev => ({ ...prev, BANKNIFTY: bnkWt.value }));
      if (niftySent.status === 'fulfilled') setSentiment(prev => ({ ...prev, NIFTY: niftySent.value }));
      if (bnkSent.status === 'fulfilled') setSentiment(prev => ({ ...prev, BANKNIFTY: bnkSent.value }));
      if (niftyFeeds.status === 'fulfilled') setTwitterFeeds(prev => ({ ...prev, NIFTY: Array.isArray(niftyFeeds.value) ? niftyFeeds.value : [] }));
      if (bnkFeeds.status === 'fulfilled') setTwitterFeeds(prev => ({ ...prev, BANKNIFTY: Array.isArray(bnkFeeds.value) ? bnkFeeds.value : [] }));
      if (niftyHist.status === 'fulfilled') setSignalHistory(prev => ({ ...prev, NIFTY: Array.isArray(niftyHist.value) ? niftyHist.value : [] }));
      if (bnkHist.status === 'fulfilled') setSignalHistory(prev => ({ ...prev, BANKNIFTY: Array.isArray(bnkHist.value) ? bnkHist.value : [] }));

      setLastUpdated(new Date());
      setMarketStatus(isMarketOpen());
    } catch (e) {
      console.error('[AI Trader] Fetch error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const handleRefreshSignal = async (symbol: string) => {
    setSignalRefreshing(cur => ({ ...cur, [symbol]: true }));
    try {
      const sig = await obs2p(get(AI_TRADER_URLS.SIGNAL(symbol, true), { showLoading: false }));
      const prev = signals[symbol];
      setSignals(cur => ({ ...cur, [symbol]: sig }));

      if (prev && sig && prev.direction !== sig.direction && sig.direction !== 'WAIT') {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`${symbol} Signal Changed!`, {
            body: `${prev.direction} → ${sig.direction} | Strike: ${sig.strike} | Confidence: ${sig.confidence}/10`,
            icon: '/favicon.ico',
          });
        }
      }
    } finally {
      setSignalRefreshing(cur => ({ ...cur, [symbol]: false }));
    }
  };

  const handleRefreshGlobal = async () => {
    setGlobalRefreshing(true);
    try {
      await obs2p(post(AI_TRADER_URLS.REFRESH_GLOBAL, {}, { showLoading: false }));
      await new Promise(res => setTimeout(res, 5000));
      const result = await obs2p(get(AI_TRADER_URLS.GLOBAL_MARKET, { showLoading: false }));
      if (result) setGlobalMarket(result);
    } catch (e) {
      console.error('[AI Trader] Global market refresh error:', e);
    } finally {
      setGlobalRefreshing(false);
    }
  };

  const handleRefreshSentiment = async () => {
    setSentimentRefreshing(true);
    try {
      await obs2p(post(AI_TRADER_URLS.REFRESH_SENTIMENT, {}, { showLoading: false }));
      // Wait for Grok API to complete and DB to be updated
      await new Promise(res => setTimeout(res, 6000));
      const [niftySent, bnkSent, niftyFeeds, bnkFeeds] = await Promise.allSettled([
        obs2p(get(AI_TRADER_URLS.SENTIMENT('NIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT('BANKNIFTY'), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT_FEEDS('NIFTY', 15), { showLoading: false })),
        obs2p(get(AI_TRADER_URLS.SENTIMENT_FEEDS('BANKNIFTY', 15), { showLoading: false })),
      ]);
      if (niftySent.status === 'fulfilled') setSentiment(prev => ({ ...prev, NIFTY: niftySent.value }));
      if (bnkSent.status === 'fulfilled') setSentiment(prev => ({ ...prev, BANKNIFTY: bnkSent.value }));
      if (niftyFeeds.status === 'fulfilled') setTwitterFeeds(prev => ({ ...prev, NIFTY: Array.isArray(niftyFeeds.value) ? niftyFeeds.value : [] }));
      if (bnkFeeds.status === 'fulfilled') setTwitterFeeds(prev => ({ ...prev, BANKNIFTY: Array.isArray(bnkFeeds.value) ? bnkFeeds.value : [] }));
      setSentimentLastUpdated(new Date());
    } catch (e) {
      console.error('[AI Trader] Sentiment refresh error:', e);
    } finally {
      setSentimentRefreshing(false);
    }
  };

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Update market status every minute
  useEffect(() => {
    const id = setInterval(() => setMarketStatus(isMarketOpen()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchAll, AUTO_REFRESH_INTERVAL);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchAll]);

  const niftySignal = signals['NIFTY'];
  const bnkSignal = signals['BANKNIFTY'];
  const activeOI = oiLevels[activeSymbol] || {};
  const activeOIChain = oiChains[activeSymbol] || null;
  const formatTime = (d: Date | null) =>
    d?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) || '';
  const statusColor = { OPEN: '#26a65b', CLOSED: '#e74c3c', PRE: '#f39c12' }[marketStatus];

  return (
    <div style={{ background: '#080810', minHeight: '100vh', padding: '0 0 2rem' }}>
      {/* Top bar */}
      <div style={{
        background: '#0a0a14', borderBottom: '1px solid #1a1a2a',
        padding: isMobile ? '6px 10px' : '8px 16px', display: 'flex', alignItems: 'center',
        gap: isMobile ? 8 : 12, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ fontWeight: 800, color: '#ddd', fontSize: '0.85rem', letterSpacing: 1 }}>
          AI TRADER
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
          <span style={{ color: statusColor, fontSize: '0.65rem', fontWeight: 700 }}>{marketStatus}</span>
        </div>
        {niftySignal?.spot_price && (
          <div style={{ color: '#ddd', fontSize: '0.72rem' }}>
            NIFTY: <span style={{ fontWeight: 700 }}>{niftySignal.spot_price?.toLocaleString('en-IN')}</span>
          </div>
        )}
        {bnkSignal?.spot_price && (
          <div style={{ color: '#ddd', fontSize: '0.72rem' }}>
            BANKNIFTY: <span style={{ fontWeight: 700 }}>{bnkSignal.spot_price?.toLocaleString('en-IN')}</span>
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastUpdated && (
            <span style={{ color: '#444', fontSize: '0.62rem' }}>Updated {formatTime(lastUpdated)}</span>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} style={{ accentColor: '#26a65b' }} />
            <span style={{ color: '#666', fontSize: '0.62rem' }}>Auto 30min</span>
          </label>
          <button onClick={fetchAll} disabled={refreshing} style={{
            background: '#1a3a5c', border: 'none', color: '#4fc3f7',
            padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: '0.65rem',
            opacity: refreshing ? 0.6 : 1,
          }}>
            {refreshing ? '⟳ Refreshing...' : '↺ Refresh All'}
          </button>
        </div>
      </div>

      <div style={{ padding: isMobile ? '8px 8px' : '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Global markets */}
        <GlobalMarketsBar data={globalMarket} loading={loading} refreshing={globalRefreshing} onRefresh={handleRefreshGlobal} />

        {/* Morning Briefing */}
        <MorningBriefing briefing={briefing} loading={loading} onRefresh={fetchAll} />

        {/* AI Decision panels */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
          <AIDecisionPanel symbol="NIFTY" signal={niftySignal} loading={loading} refreshing={signalRefreshing['NIFTY'] || false} marketStatus={marketStatus} onRefresh={() => handleRefreshSignal('NIFTY')} />
          <AIDecisionPanel symbol="BANKNIFTY" signal={bnkSignal} loading={loading} refreshing={signalRefreshing['BANKNIFTY'] || false} marketStatus={marketStatus} onRefresh={() => handleRefreshSignal('BANKNIFTY')} />
        </div>

        {/* Signal Scorecard */}
        <div>
          <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>SIGNAL SCORECARD</div>
          <SignalScorecard
            niftyScores={niftySignal?.scores}
            bankniftyScores={bnkSignal?.scores}
            niftyPCR={oiLevels.NIFTY?.pcr}
            bankniftyPCR={oiLevels.BANKNIFTY?.pcr}
          />
        </div>

        {/* Chart + OI Levels */}
        <div style={{ background: '#0d0d14', border: '1px solid #1a1a2a', borderRadius: 8, padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1 }}>CHART + OI LEVELS</div>
            {(['NIFTY', 'BANKNIFTY'] as const).map(sym => (
              <button key={sym} onClick={() => setActiveSymbol(sym)} style={{
                background: activeSymbol === sym ? '#1a3a5c' : 'transparent',
                border: `1px solid ${activeSymbol === sym ? '#4fc3f7' : '#2a2a3a'}`,
                color: activeSymbol === sym ? '#4fc3f7' : '#555',
                fontSize: '0.62rem', padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
              }}>{sym}</button>
            ))}
          </div>
          <OILevelsChart symbol={activeSymbol} oiLevels={activeOI} oiChain={activeOIChain} />
        </div>

        {/* Index Weight Map — full width with built-in NIFTY/BANKNIFTY tabs */}
        <div style={{ background: '#0d0d14', border: '1px solid #1a1a2a', borderRadius: 8, padding: 12 }}>
          <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>
            INDEX COMPANIES — TREND STATUS
          </div>
          <IndexWeightMap
            niftyData={weightMaps['NIFTY'] || null}
            bankniftyData={weightMaps['BANKNIFTY'] || null}
            loading={loading}
            onRefresh={fetchAll}
          />
        </div>

        {/* Live Feeds + Key Levels */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.6fr 1fr', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1 }}>LIVE FEEDS — GROK SENTIMENT</div>
              <button
                onClick={handleRefreshSentiment}
                disabled={sentimentRefreshing}
                title="Refresh Grok sentiment (calls xAI API)"
                style={{
                  background: 'transparent', border: '1px solid #1a2a1a',
                  color: sentimentRefreshing ? '#666' : '#4caf50',
                  fontSize: '0.6rem', padding: '2px 8px', borderRadius: 4,
                  cursor: sentimentRefreshing ? 'default' : 'pointer',
                  opacity: sentimentRefreshing ? 0.5 : 1,
                }}
              >
                {sentimentRefreshing ? '⟳ Fetching...' : '↺ Refresh Sentiment'}
              </button>
              {sentimentLastUpdated && !sentimentRefreshing && (
                <span style={{ color: '#333', fontSize: '0.58rem' }}>
                  {sentimentLastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <LiveFeeds
              twitterFeeds={twitterFeeds[activeSymbol] || []}
              newsFeeds={[]}
              keyThemes={sentiment[activeSymbol]?.key_themes || []}
            />
          </div>

          <div style={{ background: '#0d0d14', border: '1px solid #1a1a2a', borderRadius: 8, padding: 12 }}>
            <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>KEY LEVELS — {activeSymbol}</div>
            {(activeOI.resistance || []).slice(0, 2).map((r: number, i: number, arr: number[]) => (
              <div key={`r${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #111' }}>
                {/* resistance sorted desc: i=0 = farthest (Weak), last = nearest to price (Strong) */}
                <span style={{ color: '#e74c3c', fontSize: '0.68rem' }}>{i === arr.length - 1 ? 'Strong Resistance' : 'Weak Resistance'}</span>
                <span style={{ color: '#ddd', fontSize: '0.72rem', fontWeight: 700 }}>{r?.toLocaleString('en-IN')}</span>
              </div>
            ))}
            {activeOI.spot_price && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #111', background: '#1a1a0d' }}>
                <span style={{ color: '#f39c12', fontSize: '0.68rem', fontWeight: 700 }}>▶ Current Price</span>
                <span style={{ color: '#f39c12', fontSize: '0.75rem', fontWeight: 800 }}>{activeOI.spot_price?.toLocaleString('en-IN')}</span>
              </div>
            )}
            {activeOI.max_pain && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #111' }}>
                <span style={{ color: '#f39c12', fontSize: '0.65rem' }}>Max Pain</span>
                <span style={{ color: '#f39c12', fontSize: '0.72rem', fontWeight: 700 }}>{activeOI.max_pain?.toLocaleString('en-IN')}</span>
              </div>
            )}
            {(activeOI.support || []).slice(0, 2).map((s: number, i: number, arr: number[]) => (
              <div key={`s${i}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #111' }}>
                {/* support sorted desc: i=0 = nearest to price (Weak), last = farthest (Strong) */}
                <span style={{ color: '#26a65b', fontSize: '0.68rem' }}>{i === arr.length - 1 ? 'Strong Support' : 'Weak Support'}</span>
                <span style={{ color: '#ddd', fontSize: '0.72rem', fontWeight: 700 }}>{s?.toLocaleString('en-IN')}</span>
              </div>
            ))}
            {activeOI.pcr && (
              <div style={{ marginTop: 8, padding: '6px 0', borderTop: '1px solid #222' }}>
                <span style={{ color: '#777', fontSize: '0.65rem' }}>PCR: </span>
                <span style={{
                  color: activeOI.pcr > 1.2 ? '#26a65b' : activeOI.pcr < 0.8 ? '#e74c3c' : '#f39c12',
                  fontSize: '0.72rem', fontWeight: 700,
                }}>
                  {activeOI.pcr?.toFixed(2)} {activeOI.pcr > 1.2 ? '▲ Bullish' : activeOI.pcr < 0.8 ? '▼ Bearish' : '— Neutral'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Signal History */}
        <SignalHistory
          niftyHistory={signalHistory['NIFTY'] || []}
          bankniftyHistory={signalHistory['BANKNIFTY'] || []}
        />

        {/* AI Chat */}
        <div style={{ background: '#0d0d14', border: '1px solid #1a1a2a', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1 }}>AI CHAT (GROK)</div>
            {(['NIFTY', 'BANKNIFTY'] as const).map(sym => (
              <button key={sym} onClick={() => setActiveSymbol(sym)} style={{
                background: activeSymbol === sym ? '#0d1a2a' : 'transparent',
                border: `1px solid ${activeSymbol === sym ? '#4fc3f7' : '#2a2a3a'}`,
                color: activeSymbol === sym ? '#4fc3f7' : '#555',
                fontSize: '0.62rem', padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
              }}>{sym}</button>
            ))}
          </div>
          <AITraderChat symbol={activeSymbol} />
        </div>
      </div>
    </div>
  );
}
